import { useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import sendNodeCommand from 'store/sendNodeCommand';

type DisposeAction = 'cleanup' | 'remove';

interface DisposeWorkerDialogProps {
  action: DisposeAction;
  nodeAgentId: string;
  instanceId: string;
  workerName: string;
  onClose: () => void;
  onConfirm: () => void;
}

const COPY = {
  cleanup: {
    title: 'Clean Up Worker',
    command: 'cleanup_worker' as const,
    button: 'Clean Up',
    busy: 'Cleaning up\u2026',
    failure: 'Failed to clean up worker. Please try again.',
  },
  remove: {
    title: 'Remove Worker',
    command: 'remove_worker' as const,
    button: 'Remove',
    busy: 'Removing\u2026',
    failure: 'Failed to remove worker. Please try again.',
  },
};

export default function DisposeWorkerDialog({
  action,
  nodeAgentId,
  instanceId,
  workerName,
  onClose,
  onConfirm,
}: DisposeWorkerDialogProps) {
  const copy = COPY[action];
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await sendNodeCommand(nodeAgentId, copy.command, {
        instance_id: instanceId,
      });
      onConfirm();
      onClose();
    } catch (err) {
      console.error(`Failed to ${action} worker:`, err);
      setError(copy.failure);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {action === 'remove' ? (
            <>
              This will stop worker <strong>{workerName}</strong> if it is still running, and discard it.
            </>
          ) : (
            <>
              This will remove worker <strong>{workerName}</strong> from tracking.
            </>
          )}{' '}
          Its files are erased, and if no other workers share its working directory that directory is deleted too (venv,
          log file and task run files), keeping any run files the backend still has registered. This cannot be undone.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="error" disabled={isSubmitting}>
          {isSubmitting ? copy.busy : copy.button}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
